import Hls from 'hls.js'
import { clsx } from 'clsx'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatPauseTime } from '../formatPauseTime'

export interface PauseMarker {
  quizId: string
  pauseTimeMs: number
  state: 'correct' | 'partial' | 'incorrect' | 'unanswered' | 'pending'
}

export interface CoachingVideoPlayerProps {
  videoUrl?: string
  thumbUrl?: string
  markers?: PauseMarker[]
  onMarkerClick?: (quizId: string) => void
  seekTrigger?: { pauseTimeMs: number; nonce: number }
}

// 백엔드 HLS 엔드포인트(/content/{id}/quetures/{qid}/hls)는 확장자가 없으므로 경로 끝 /hls도 HLS로 취급한다.
const HLS_URL_RE = /(\.m3u8|\/hls)($|\?)/i

function isHls(url: string): boolean {
  return HLS_URL_RE.test(url)
}

const STATE_COLOR: Record<PauseMarker['state'], string> = {
  correct: 'bg-green-500',
  partial: 'bg-amber-500',
  incorrect: 'bg-red-500',
  unanswered: 'bg-gray-400',
  pending: 'bg-amber-500',
}

export function CoachingVideoPlayer({
  videoUrl,
  thumbUrl,
  markers,
  onMarkerClick,
  seekTrigger,
}: CoachingVideoPlayerProps) {
  const { t } = useTranslation('coaching')
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [durationMs, setDurationMs] = useState<number | null>(null)

  useEffect(() => {
    if (!videoUrl) return
    const video = videoRef.current
    if (!video) return
    if (!isHls(videoUrl)) return

    // hls.js(MSE) 우선. Chrome도 canPlayType('application/vnd.apple.mpegurl')에 'maybe'를
    // 반환하지만 실제 네이티브 재생은 안 되므로, 네이티브 HLS는 hls.js 미지원 환경(iOS Safari)에서만 쓴다.
    if (!Hls.isSupported()) {
      video.src = videoUrl
      return
    }

    const hls = new Hls()
    hls.loadSource(videoUrl)
    hls.attachMedia(video)
    return () => {
      hls.destroy()
    }
  }, [videoUrl])

  const seekNonce = seekTrigger?.nonce
  const seekPauseTimeMs = seekTrigger?.pauseTimeMs

  useEffect(() => {
    if (seekNonce == null || seekPauseTimeMs == null) return
    const video = videoRef.current
    if (!video) return
    video.currentTime = seekPauseTimeMs / 1000
  }, [seekNonce, seekPauseTimeMs])

  if (!videoUrl) {
    return (
      <div
        className="aspect-video w-full rounded border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-xs text-gray-500"
        role="status"
      >
        {t('video.urlMissing')}
      </div>
    )
  }

  const isHlsUrl = isHls(videoUrl)

  const handleLoadedMetadata = () => {
    const video = videoRef.current
    if (!video) return
    const seconds = video.duration
    if (Number.isFinite(seconds) && seconds > 0) {
      setDurationMs(seconds * 1000)
    }
  }

  return (
    <div className="space-y-1">
      <div className="relative w-full aspect-video rounded overflow-hidden bg-black">
        <video
          ref={videoRef}
          data-testid="coaching-video-element"
          className="w-full h-full"
          src={isHlsUrl ? undefined : videoUrl}
          poster={thumbUrl}
          controls
          preload="metadata"
          onLoadedMetadata={handleLoadedMetadata}
        />
      </div>
      {markers && markers.length > 0 ? (
        <div className="relative h-3 mx-1" aria-label="pause-markers">
          {markers.map((marker) => {
            const leftPct = durationMs
              ? Math.min(100, Math.max(0, (marker.pauseTimeMs / durationMs) * 100))
              : 0
            const time = formatPauseTime(marker.pauseTimeMs, t)
            return (
              <button
                key={marker.quizId}
                type="button"
                data-testid="coaching-video-marker"
                className={clsx(
                  'absolute top-1/2 -translate-x-1/2 -translate-y-1/2',
                  'w-3 h-3 rounded-full ring-2 ring-white shadow',
                  'hover:scale-125 transition-transform',
                  STATE_COLOR[marker.state],
                )}
                style={{ left: `${leftPct}%` }}
                aria-label={t('video.markerAriaLabel', { time })}
                onClick={() => onMarkerClick?.(marker.quizId)}
              />
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
